import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import Cookies from "js-cookie";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import {
  listBophansudung,
  url_create_team,
  url_get_list_team,
  url_update_team,
} from "../constants";
import { authAxios } from "../utils";

const { Search } = Input;

export default function SettingsTeam() {
  const [listTeam, setListTeam] = useState([]);
  const [bophan, setBoPhan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchListTeam();
    fetchBophansudung();
  }, []);

  const fetchListTeam = async () => {
    try {
      setLoading(true);
      const res = await authAxios(Cookies.get("token")).get(url_get_list_team);
      setListTeam(res.data || []);
    } catch (error) {
      console.error(error);
      message.error("Không thể tải danh sách team");
    } finally {
      setLoading(false);
    }
  };

  const fetchBophansudung = async () => {
    try {
      const res = await authAxios(Cookies.get("token")).get(listBophansudung, {
        params: { is_active: true, page: 1, page_size: 100 },
      });

      setBoPhan(res.data.results ?? []);
    } catch (err) {
      console.error(err);
      message.error("Không thể tải danh sách bộ phận");

      if (err.response?.status === 401) {
        window.location.href = "/login";
      }
    }
  };

  // Filter theo keyword
  const dataSource = useMemo(() => {
    if (!keyword) return listTeam;
    return listTeam.filter((item) =>
      item.name?.toLowerCase().includes(keyword.toLowerCase()),
    );
  }, [listTeam, keyword]);

  const handleOpenModal = (team = null) => {
    setEditingTeam(team);
    if (team) {
      form.setFieldsValue({
        name: team.name,
        bophan: team.bophan_id || team.bophan,
        is_active: team.is_active,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTeam(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingTeam) {
        await authAxios(Cookies.get("token")).put(
          `${url_update_team}/${editingTeam.id}`,
          {
            ...values,
          },
        );
        message.success("Cập nhật team thành công");
      } else {
        await authAxios(Cookies.get("token")).post(url_create_team, values);
        message.success("Thêm team thành công");
      }
      handleCloseModal();
      fetchListTeam();
    } catch (error) {
      console.error(error);
      message.error(
        editingTeam ? "Không thể cập nhật team" : "Không thể tạo team",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Tìm tên bộ phận từ ID
  const getBophanName = (bophanId) => {
    const found = bophan.find((item) => item.id === bophanId);
    return found?.name || "-";
  };

  const columns = [
    {
      title: "STT",
      key: "stt",
      width: 70,
      align: "center",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Tên Team",
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: "Bộ phận",
      dataIndex: "bophan",
      key: "bophan",
      align: "center",
      render: (bophanId) => getBophanName(bophanId),
    },
    {
      title: "Ngày tạo",
      dataIndex: "created",
      key: "created",
      align: "center",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Trạng thái",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive) => <span>{isActive ? "Kích hoạt" : "Khóa"}</span>,
    },
    {
      title: "Thao tác",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          >
            Sửa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col sm={14}>
          <Button type="primary" onClick={() => handleOpenModal()}>
            <PlusOutlined />
            Thêm team
          </Button>
        </Col>
        <Col sm={10}>
          <Search
            placeholder="Nhập từ khóa tìm kiếm"
            enterButton="Tìm kiếm"
            allowClear
            onSearch={(value) => {
              setKeyword(value.trim());
              setCurrentPage(1);
            }}
          />
        </Col>
      </Row>

      <Table
        rowKey="id"
        bordered
        loading={loading}
        dataSource={dataSource}
        columns={columns}
        onChange={handleTableChange}
        scroll={{ x: 1000, y: "70vh" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          total: dataSource.length,
        }}
      />

      <Modal
        title={editingTeam ? "Chỉnh sửa team" : "Thêm team mới"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Tên team"
            name="name"
            rules={[
              { required: true, message: "Vui lòng nhập tên team" },
              { min: 2, message: "Tên team phải có ít nhất 2 ký tự" },
            ]}
          >
            <Input placeholder="Nhập tên team" />
          </Form.Item>

          <Form.Item
            label="Bộ phận"
            name="bophan"
            rules={[{ required: true, message: "Vui lòng chọn bộ phận" }]}
          >
            <Select
              placeholder="Chọn bộ phận"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={bophan.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </Form.Item>

          <Form.Item name="is_active" valuePropName="checked" label="Kích hoạt">
            <Checkbox>Kích hoạt</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleCloseModal}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingTeam ? "Cập nhật" : "Thêm mới"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
